import * as React from 'react';
import TextField from '@mui/material/TextField';
import { NumericFormat } from 'react-number-format';
import { useIntl } from 'react-intl';

import NumericInputHelper from 'app/components/FormikField/Text/NumericInputHelper';
import IdentifierInputHelper from 'app/components/FormikField/Text/IdentifierInputHelper';
import { isNil } from 'app/utils';

import type { RegulationsField } from './RegulationsField.types';
import { useRegulationsFieldValue } from './useRegulationsFieldValue';

export type RegulationsTextFieldProps = {
  field: RegulationsField;
  className?: string;
  variant?: string;
  onChange?: (value: any) => any;
};

const NUMERIC_TYPES = new Set(['number', 'float', 'currency']);

const RegulationsTextFieldComponent = ({ field, className, variant, onChange }: RegulationsTextFieldProps) => {
  const intl = useIntl();
  const {
    name,
    value,
    defaultValue,
    type = 'text',
    options = {},
    required = false,
    disabled = false,
    fullWidth = true,
  } = field;

  const initialValue = React.useMemo(() => {
    const source = isNil(value) ? defaultValue : value;
    return isNil(source) ? '' : source;
  }, [value, defaultValue]);

  const [currentValue, commit] = useRegulationsFieldValue(name, initialValue);

  const isNumber = NUMERIC_TYPES.has(type);
  const isIdentifier = type === 'identifier';

  const numericHelper = React.useMemo(() => new NumericInputHelper(intl.locale, options), [intl.locale, options]);
  const identifierHelper = React.useMemo(() => new IdentifierInputHelper(intl.locale, options), [intl.locale, options]);

  const size = variant === 'slim' ? 'small' : undefined;
  const isNarrow = options.size === 'small';
  const inputStyle = {
    ...(options.textAlign ? { textAlign: options.textAlign } : {}),
    ...(isNarrow ? { width: '60px' } : {}),
  };
  const rootSx = isNarrow ? { '& .MuiInputBase-root': { minWidth: 'unset' } } : undefined;

  const commitValue = React.useCallback(
    (rawValue: any) => {
      const nextValue = onChange ? onChange(rawValue) ?? rawValue : rawValue;
      commit(nextValue);
    },
    [commit, onChange],
  );

  const onTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = isIdentifier ? identifierHelper.getValue(event.target.value) : event.target.value;
      commitValue(newValue);
    },
    [commitValue, identifierHelper, isIdentifier],
  );

  if (isNumber) {
    return (
      <NumericFormat
        className={className}
        customInput={TextField}
        size={size}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        sx={rootSx}
        inputProps={{ style: inputStyle }}
        value={numericHelper.getValue(currentValue)}
        onValueChange={values => commitValue(numericHelper.getValue(values.value))}
        {...numericHelper.getProps()}
      />
    );
  }

  return (
    <TextField
      className={className}
      size={size}
      fullWidth={fullWidth}
      required={required}
      disabled={disabled}
      type={type === 'password' ? 'password' : 'text'}
      sx={rootSx}
      inputProps={{ style: inputStyle }}
      value={currentValue ?? ''}
      onChange={onTextChange}
    />
  );
};

export const RegulationsTextField = React.memo(RegulationsTextFieldComponent);
